import { z } from 'zod';
import { planCoverageSchema } from './storey-level.model';

/**
 * Visual family of a cropped drawing. Finer than classifier
 * `blueprint_subtype` (`top_view` | `side_view`).
 */
export const extractedDrawingKindSchema = z.enum([
  'plan',
  'section',
  'elevation',
  'other',
]);
export type ExtractedDrawingKind = z.infer<typeof extractedDrawingKindSchema>;

/**
 * What a plan drawing shows. `floor_plan` is a storey drawn with its walls and
 * rooms. `area_diagram` is a storey shown as areas for an area calculation.
 * `site_plan` is the site with the buildings in their surroundings.
 */
export const extractedPlanViewSchema = z.enum([
  'floor_plan',
  'area_diagram',
  'site_plan',
]);
export type ExtractedPlanView = z.infer<typeof extractedPlanViewSchema>;

/**
 * Storey identity for plan drawings. `top` is the uppermost floor only.
 * `typical` is an intermediate floor, including a numbered plan with its own
 * sheet that is not the entrance, not uppermost, and not below ground.
 * `entrance` is the entrance level. `unknown` when the role cannot be read.
 * Sections and elevations stay `unknown`.
 */
export const extractedDrawingStoreyKindSchema = z.enum([
  'cellar',
  'entrance',
  'typical',
  'top',
  'roof',
  'courtyard',
  'unknown',
]);
export type ExtractedDrawingStoreyKind = z.infer<
  typeof extractedDrawingStoreyKindSchema
>;

/** Normalized page rectangle, origin top-left, each field in 0..1. */
export const extractedDrawingBboxSchema = z.object({
  left: z.number(),
  top: z.number(),
  width: z.number(),
  height: z.number(),
});
export type ExtractedDrawingBbox = z.infer<typeof extractedDrawingBboxSchema>;

/**
 * Rectangle on a page. PDF: user units in the scale-1 viewport (includes
 * page rotation), origin top-left, +y down. Image upload: source pixels.
 * May sit outside the page; that area is blank paper.
 */
export const pageRectSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
});
export type PageRect = z.infer<typeof pageRectSchema>;

export const extractedDrawingPageFrameSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('pdf'),
    widthPt: z.number().positive(),
    heightPt: z.number().positive(),
    rotationDeg: z.union([
      z.literal(0),
      z.literal(90),
      z.literal(180),
      z.literal(270),
    ]),
  }),
  z.object({
    kind: z.literal('image'),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
  }),
]);
export type ExtractedDrawingPageFrame = z.infer<
  typeof extractedDrawingPageFrameSchema
>;

/** One immutable raster for a file page. Drawings store coordinates on it. */
export const projectFilePageRasterSchema = z.object({
  fileId: z.string(),
  pageIndex: z.number().int().nonnegative(),
  pageFrame: extractedDrawingPageFrameSchema,
  blobRef: z.string(),
  format: z.enum(['webp', 'jpeg', 'png']),
  widthPx: z.number().int().positive(),
  heightPx: z.number().int().positive(),
  /** Pixels per paper inch. Null for an image upload (1 source px = 1 unit). */
  dpi: z.number().positive().nullable(),
  renderer: z.string(),
});
export type ProjectFilePageRaster = z.infer<typeof projectFilePageRasterSchema>;

export const planAlignmentUnitSchema = z.enum(['metre', 'pixel']);
export type PlanAlignmentUnit = z.infer<typeof planAlignmentUnitSchema>;

/**
 * Where this crop's top-left sits relative to the anchor crop's top-left.
 * Written once when the batch is aligned. Null when the crop does not share
 * a frame with the anchor.
 */
export const planAlignmentSchema = z.object({
  anchorDrawingId: z.string(),
  unit: planAlignmentUnitSchema,
  offsetX: z.number(),
  offsetY: z.number(),
});
export type PlanAlignment = z.infer<typeof planAlignmentSchema>;

/** One saved placement per alignment method, so the viewer can switch. */
export const planAlignmentSetSchema = z.object({
  page: planAlignmentSchema.nullable(),
  ink: planAlignmentSchema.nullable(),
  combined: planAlignmentSchema.nullable(),
});
export type PlanAlignmentSet = z.infer<typeof planAlignmentSetSchema>;

/** Where a paper size was read. */
export const paperClaimSourceSchema = z.enum([
  'scale_parenthesis',
  'page_measurement',
  'title_block_token',
  'drawing_extent',
  'repeated_sheet',
  'file_name',
]);
export type PaperClaimSource = z.infer<typeof paperClaimSourceSchema>;

export const scaleSourceSchema = z.enum(['printed_scale']);
export type ScaleSource = z.infer<typeof scaleSourceSchema>;

/** Where a drawing's pixels per metre came from. */
export const metreScaleSourceSchema = z.enum([
  /** The printed scale bar, measured on the raster. */
  'scale_bar',
  /** The printed `1:N` on the paper size the page was measured as. */
  'paper_and_ratio',
  /** Printed dimensions that agree with each other, measured on the raster. */
  'dimensions',
]);
export type MetreScaleSource = z.infer<typeof metreScaleSourceSchema>;

/** One reading of a drawing's pixels per metre. A source that read nothing is absent. */
export const metreScaleClaimSchema = z.object({
  source: metreScaleSourceSchema,
  pixelsPerMeter: z.number().positive(),
});
export type MetreScaleClaim = z.infer<typeof metreScaleClaimSchema>;

/**
 * How a north direction was read, most to least trustworthy:
 * - `north_arrow`: a north arrow or compass rose printed for the drawing.
 * - `coordinate_grid`: map coordinates or a coordinate grid printed on it.
 * - `matched_drawing`: the drawing shows no north of its own; it is turned
 *   until the building matches another drawing of it whose north is read.
 *   It is as sure as that drawing's north and the match.
 * - `assumed`: nothing gives north; page up is taken as north.
 * - `none`: nothing gives north, and no direction is given.
 */
export const northMethodSchema = z.enum([
  'north_arrow',
  'coordinate_grid',
  'matched_drawing',
  'assumed',
  'none',
]);
export type NorthMethod = z.infer<typeof northMethodSchema>;

/** North on a drawing and how it was read. */
export const drawingNorthSchema = z.object({
  /** Degrees clockwise from page up to north. Null when {@link method} is `none`. */
  deg: z.number().nullable(),
  method: northMethodSchema,
});
export type DrawingNorth = z.infer<typeof drawingNorthSchema>;

/** The scale bar as read on the page: its 0 tick and a labelled tick, as page fractions. */
export const scaleBarReadSchema = z.object({
  zero_x: z.number(),
  zero_y: z.number(),
  end_x: z.number(),
  end_y: z.number(),
  end_m: z.number(),
});
export type ScaleBarRead = z.infer<typeof scaleBarReadSchema>;

/** One printed dimension as read on the page: its two ends, as page fractions, and the printed length. */
export const dimensionReadSchema = z.object({
  start_x: z.number(),
  start_y: z.number(),
  end_x: z.number(),
  end_y: z.number(),
  length_mm: z.number(),
});
export type DimensionRead = z.infer<typeof dimensionReadSchema>;

/**
 * One paper-size reading. A source that did not read a value is absent.
 * `value` is null when the source measured millimetres that are not a sheet.
 */
export const paperClaimSchema = z.object({
  source: paperClaimSourceSchema,
  value: z.string().nullable(),
  widthMm: z.number().positive().optional(),
  heightMm: z.number().positive().optional(),
  cleanliness: z.number(),
  methodWeight: z.number(),
  score: z.number(),
});
export type PaperClaim = z.infer<typeof paperClaimSchema>;

export const drawingFixedFeatureKindSchema = z.enum([
  'stair',
  'lift',
  'shaft',
  'grid',
]);
export type DrawingFixedFeatureKind = z.infer<
  typeof drawingFixedFeatureKindSchema
>;

/** A feature that stands in the same place on every storey of a building. */
export const drawingFixedFeatureSchema = z.object({
  kind: drawingFixedFeatureKindSchema,
  /** As printed: `A/3` for a grid crossing, `TR1` for a stair. */
  label: z.string().nullable(),
  /** The walls around a stairwell, lift or shaft; one point for a grid crossing. */
  outline: z.array(z.tuple([z.number(), z.number()])),
});
export type DrawingFixedFeature = z.infer<typeof drawingFixedFeatureSchema>;

/** One traced polygon on a plan crop. Points are fractions of the traced region, origin top-left. */
export const drawingFootprintPolygonSchema = z.object({
  cutout: z.boolean(),
  /**
   * Buildings or parts the polygon outlines, from the drawing's
   * `buildingInstanceIds`: several when one outline spans parts joined wall
   * to wall. Absent or empty when the drawing shows one building or the model
   * could not tell.
   */
  buildingKeys: z.array(z.string()).optional(),
  /** The single id footprints traced before `buildingKeys` stored. */
  buildingKey: z.string().nullable().optional(),
  points: z.array(z.tuple([z.number(), z.number()])),
  /**
   * Areas (m²) printed inside the polygon: apartment, room and premises
   * labels. Judges the sheet's scale. Absent on footprints traced before.
   */
  printedAreasM2: z.array(z.number().positive()).optional(),
  /** Name the plan prints for the polygon's building. Absent on footprints traced before. */
  printedName: z.string().nullable().optional(),
  /**
   * The model's number for the polygon's building on a plan of several: the
   * buildings in reading order of where they stand, numbered the same way on
   * every plan of the site. Absent on footprints traced before.
   */
  siteNumber: z.number().int().positive().nullable().optional(),
  /**
   * Stairwells, lift shafts, installation shafts and grid crossings inside
   * the polygon, which stand still from storey to storey. Points are
   * fractions of the traced region. Absent on footprints traced before.
   */
  fixedFeatures: z.array(drawingFixedFeatureSchema).optional(),
  /**
   * The outline of the storey below where this plan draws it, often dashed,
   * as fractions of the traced region. Absent when the plan does not draw it.
   */
  outlineBelow: z.array(z.tuple([z.number(), z.number()])).optional(),
});
export type DrawingFootprintPolygon = z.infer<
  typeof drawingFootprintPolygonSchema
>;

export const drawingFootprintSchema = z.object({
  /**
   * Region on the page that was traced; the points are fractions of it.
   * Absent on footprints traced on the drawing's own {@link pageRectSchema rectPt}.
   */
  regionPt: pageRectSchema.optional(),
  polygons: z.array(drawingFootprintPolygonSchema),
});
export type DrawingFootprint = z.infer<typeof drawingFootprintSchema>;

/**
 * The model's verdict on which scale a plan sheet is drawn at, asked only
 * when the readings disagree. Stored when the question is sent (`askedAt`)
 * and again with the answer. `pixelsPerMeter` is the chosen candidate; null
 * while waiting and when the model found none plausible.
 */
export const drawingScaleJudgementSchema = z.object({
  askedAt: z.string(),
  candidates: z.array(z.number().positive()),
  pixelsPerMeter: z.number().positive().nullable(),
  confidence: z.enum(['low', 'medium', 'high']).nullable(),
  evidence: z.string().nullable(),
});
export type DrawingScaleJudgement = z.infer<typeof drawingScaleJudgementSchema>;

/** Crops stored before `planNumbers` kept the printed plan number in `storeyIndex`. */
const upgradeLegacyDrawing = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const row = value as Record<string, unknown>;
  if ('planNumbers' in row) return row;
  const { storeyIndex, ...rest } = row;
  return {
    ...rest,
    planNumbers:
      typeof storeyIndex === 'number' && Number.isInteger(storeyIndex)
        ? [storeyIndex]
        : [],
  };
};

/**
 * One drawing persisted on {@link import('./project-file-batch.model').ProjectFile}.
 * New rows are coordinates ({@link rectPt}) on the file's page raster.
 * {@link blobRef} is only set for a legacy per-drawing image.
 */
export const extractedDrawingSchema = z.preprocess(
  upgradeLegacyDrawing,
  z.object({
    id: z.string(),
    /** Legacy per-drawing image. Null when the drawing is coordinates on a page raster. */
    blobRef: z.string().nullable(),
    pageIndex: z.number().int().nonnegative(),
    /** Detection box, 0–1, unpadded. Not the stored region. */
    bbox: extractedDrawingBboxSchema,
    /** Page the coordinates are in. Null on a legacy per-drawing image. */
    pageFrame: extractedDrawingPageFrameSchema.nullable().default(null),
    /**
     * Region on {@link pageFrame} that this drawing covers, including the
     * same pad the legacy JPEG used. Null on a legacy per-drawing image.
     */
    rectPt: pageRectSchema.nullable().default(null),
    buildingInstanceId: z.string().nullable(),
    /**
     * Every known building this drawing shows. Empty when it is not tied to
     * one — a site plan, or an id that did not match a building in the batch.
     * Absent on older crops.
     */
    buildingInstanceIds: z.array(z.string()).optional(),
    buildingLabel: z.string().nullable(),
    drawingKind: extractedDrawingKindSchema,
    /** What a plan shows. Null for other kinds; absent on older crops. */
    planView: extractedPlanViewSchema.nullable().optional(),
    storeyKind: extractedDrawingStoreyKindSchema,
    /**
     * Role of this plan per building, when a plan of several buildings shows
     * a different level of each. Absent when it is {@link storeyKind} for all.
     */
    storeyKindByBuilding: z
      .record(z.string(), extractedDrawingStoreyKindSchema)
      .optional(),
    /** Printed plan numbers, ascending (`Plan 11-14` → 11…14). Empty when none are printed. */
    planNumbers: z.array(z.number().int()).default([]),
    /**
     * Storeys this plan shows. Null until it can be placed (plan numbers
     * without a known entrance) and for sections / elevations.
     */
    coverage: planCoverageSchema.nullable().default(null),
    viewLabel: z.string().nullable(),
    /**
     * Printed scale ratio (`1:50`). Paper size is {@link paper}.
     * May be wrong; do not treat as ground truth.
     */
    scale: z.string().nullable(),
    /** Where {@link scale} was read. Absent when the sheet prints no ratio. */
    scaleSource: scaleSourceSchema.nullable().optional(),
    /**
     * Paper the printed scale is measured on. Absent when no source named one.
     */
    paper: z.string().nullable().optional(),
    /** Claim {@link paper} was taken from. Absent when {@link paper} is absent. */
    paperSource: paperClaimSourceSchema.nullable().optional(),
    /**
     * Every paper claim that was read, including ones that were not chosen.
     * A missing source adds nothing.
     */
    paperClaims: z.array(paperClaimSchema).optional(),
    drawingId: z.string().nullable(),
    pixelWidth: z.number().int().positive().nullable().default(null),
    pixelHeight: z.number().int().positive().nullable().default(null),
    /** Physical size of the stored JPEG (padded crop). Null for raster uploads. */
    paperWidthMm: z.number().positive().nullable().default(null),
    paperHeightMm: z.number().positive().nullable().default(null),
    /**
     * PDF page box in millimetres. The raster page, which can differ from
     * {@link paper}. Absent for a raster upload and for crops stored before
     * the page box was kept.
     */
    pageWidthMm: z.number().positive().nullable().optional(),
    pageHeightMm: z.number().positive().nullable().optional(),
    /** Actual raster DPI after the page-edge cap. Null for raster uploads. */
    renderDpi: z.number().positive().nullable().default(null),
    /**
     * Pixels per real-world metre if {@link scale} parsed as `1:N`.
     * Null when stamp is missing or unparsed — paper mm + pixels still convert.
     */
    pixelsPerMeter: z.number().positive().nullable().default(null),
    /** Every source read for {@link pixelsPerMeter}; it holds the chosen one. Absent on crops stored before. */
    scaleClaims: z.array(metreScaleClaimSchema).optional(),
    /** The printed scale bar, when the detection read one. */
    scaleBar: scaleBarReadSchema.nullable().optional(),
    dimensions: z.array(dimensionReadSchema).optional(),
    /** North as the detection read it. Absent on crops stored before. */
    north: drawingNorthSchema.optional(),
    planAlignment: planAlignmentSchema.nullable().default(null),
    planAlignments: planAlignmentSetSchema.nullable().default(null),
    /**
     * Building outline traced on this plan crop. Points are fractions of
     * {@link rectPt} (origin top-left). Absent or null until traced, and on
     * drawings that are not plans.
     */
    footprint: drawingFootprintSchema.nullable().optional(),
    /** Set by the scale check when the readings disagree. Absent otherwise. */
    scaleJudgement: drawingScaleJudgementSchema.optional(),
  }),
);
export type ExtractedDrawing = z.infer<typeof extractedDrawingSchema>;
